import type { AlgoliaCompanyRecord } from '../../types/company-search-record';
import type { CompanyProfileLocale } from '../seo/company-profile-alternate-urls';
import { mapEmployerScoreToTier } from '../employer-score/mapScoreToTier';

function fmtSalaryRange(min?: number, max?: number): string | null {
  if (min == null && max == null) return null;
  const fmt = (n: number) => `€${Math.round(n / 100) * 100}`;
  if (min != null && max != null) {
    if (min === max) return `${fmt(min)} / month`;
    return `${fmt(min)} – ${fmt(max)} / month`;
  }
  if (min != null) return `from ${fmt(min)} / month`;
  if (max != null) return `up to ${fmt(max)} / month`;
  return null;
}

export interface CompanySeoNarrative {
  /** Visible lead paragraph for crawlers and users */
  lead: string;
  /** Optional second paragraph (distributions) */
  detail?: string;
}

/**
 * Plain-language summary of the employer profile for visible SEO text (locale-aware).
 * Copy is intentionally non-definitive about legal HQ or “Finnish company” status: it reflects
 * what we see in aggregated job listings. Domicile inference can be layered in later from registry / roles.
 */
export function buildCompanySeoNarrative(
  company: AlgoliaCompanyRecord,
  displayName: string,
  locale: CompanyProfileLocale,
  industryLabel: string,
): CompanySeoNarrative {
  const parts: string[] = [];
  const loc = locale;
  // Publish the coarse letter tier, never the raw 0-100 number: mapScoreToTier exists so the
  // public-facing display does not state a specific figure an employer could dispute.
  const scoreTier =
    company.employerScore != null ? mapEmployerScoreToTier(company.employerScore) : null;

  const headcount =
    company.companyHeadCountMin != null && company.companyHeadCountMax != null
      ? `${company.companyHeadCountMin}–${company.companyHeadCountMax}`
      : company.companyHeadCountMin != null
        ? String(company.companyHeadCountMin)
        : company.companyHeadCountMax != null
          ? String(company.companyHeadCountMax)
          : '';

  if (loc === 'fi') {
    parts.push(
      `${displayName} näkyy JobCrawlsin työmarkkinadatassa perustuen työpaikkailmoituksiin, joita seuraamme Suomessa (ei välttämättä kotipaikka tai rekisteröity sijainti).`,
    );
    if (industryLabel) parts.push(`Toimiala: ${industryLabel}.`);
    if (company.registryCity) {
      parts.push(`Sijainti: ${company.registryCity}${company.registryCountry ? `, ${company.registryCountry}` : ''}.`);
    }
    if (headcount) parts.push(`Arvioitu henkilöstö: ${headcount}.`);
    if (scoreTier) {
      parts.push(`JobCrawls-työnantajapisteet: ${scoreTier}.`);
    }
    if (company.hiringTrend === 'growing') parts.push('Rekrytointi on viime kuukausina kasvussa.');
    else if (company.hiringTrend === 'declining') parts.push('Rekrytointi on viime kuukausina laskussa.');
    else parts.push('Rekrytointivolyymi on ollut suhteellisen vakaa.');
  } else if (loc === 'sv') {
    parts.push(
      `${displayName} finns i JobCrawls jobbmarknadsdata för Finland utifrån aggregerade platsannonser vi följer (säger inte var företaget är registrerat eller har huvudkontor).`,
    );
    if (industryLabel) parts.push(`Bransch: ${industryLabel}.`);
    if (company.registryCity) {
      parts.push(`Plats: ${company.registryCity}${company.registryCountry ? `, ${company.registryCountry}` : ''}.`);
    }
    if (headcount) parts.push(`Uppskattad storlek: ${headcount} anställda.`);
    if (scoreTier) {
      parts.push(`JobCrawls arbetsgivarbetyg: ${scoreTier}.`);
    }
    if (company.hiringTrend === 'growing') parts.push('Rekryteringen har ökat på senare månader.');
    else if (company.hiringTrend === 'declining') parts.push('Rekryteringen har minskat på senare månader.');
    else parts.push('Rekryteringsvolymen har varit relativt stabil.');
  } else {
    parts.push(
      `${displayName} appears in JobCrawls’ Finnish labour-market dataset based on job listings we aggregate for Finland — this does not imply where the company is headquartered.`,
    );
    if (industryLabel) parts.push(`Industry: ${industryLabel}.`);
    if (company.registryCity) {
      parts.push(`Location: ${company.registryCity}${company.registryCountry ? `, ${company.registryCountry}` : ''}.`);
    }
    if (headcount) parts.push(`Estimated headcount range: ${headcount} employees.`);
    if (scoreTier) {
      parts.push(`JobCrawls Employer Score: ${scoreTier}.`);
    }
    if (company.hiringTrend === 'growing') parts.push('Hiring volume has been trending up in recent months.');
    else if (company.hiringTrend === 'declining') {
      parts.push('Hiring volume has been trending down in recent months.');
    } else {
      parts.push('Hiring volume has been relatively stable in recent months.');
    }
  }

  const lead = parts.join(' ');

  const topRoles = company.topRoles?.slice(0, 3).filter(Boolean);
  const salaryLine = fmtSalaryRange(company.medianSalaryMin, company.medianSalaryMax);
  const rem = company.remotenessDistribution;
  const remTotal = rem ? rem.remote + rem.hybrid + rem.onsite : 0;
  const remotePct =
    rem && remTotal > 0 ? Math.round((rem.remote / remTotal) * 100) : null;

  let detail: string | undefined;
  if (loc === 'fi') {
    const bits: string[] = [];
    if (topRoles?.length) bits.push(`Yleisimmät roolit ilmoituksissa: ${topRoles.join(', ')}.`);
    if (salaryLine) bits.push(`Ilmoituksiin perustuva mediaanipalkka-alue: ${salaryLine}.`);
    if (remotePct != null) bits.push(`Etätyöilmoitusten osuus: noin ${remotePct} %.`);
    if (bits.length) detail = bits.join(' ');
  } else if (loc === 'sv') {
    const bits: string[] = [];
    if (topRoles?.length) bits.push(`Vanligaste roller i annonser: ${topRoles.join(', ')}.`);
    if (salaryLine) bits.push(`Medianlönespann från annonser: ${salaryLine}.`);
    if (remotePct != null) bits.push(`Andel distansjobb: cirka ${remotePct} %.`);
    if (bits.length) detail = bits.join(' ');
  } else {
    const bits: string[] = [];
    if (topRoles?.length) bits.push(`Top roles in listings: ${topRoles.join(', ')}.`);
    if (salaryLine) bits.push(`Median salary band from listings: ${salaryLine}.`);
    if (remotePct != null) bits.push(`Share of remote job postings: about ${remotePct}%.`);
    if (bits.length) detail = bits.join(' ');
  }

  return { lead, detail };
}
